import JenErrorToast from '@/components/ui/jen-error-toast';
import { AudioDetailDto } from '@/service/interface';
import { AudioDto } from '@/service/type';
import { toast } from 'sonner';
import { getDownloadStemsUrl, getDownloadUrl } from '../service/audioList';
import { Dialog } from '@/components/Dialog';

function transformUrlToCurrentDomain(fileUrl: string) {
  const currentDomain = window.location.origin;
  if (currentDomain === 'https://industry-portal.jenmusic.ai' && !fileUrl.startsWith('https://storage.googleapis.com')) {
    const url = new URL(fileUrl);
    url.hostname = new URL(currentDomain).hostname;
    return url.toString();
  } else {
    return fileUrl;
  }
}

export const useDownloadStemsAudio = () => {
  const downloadStems = async (audioData: AudioDto | AudioDetailDto) => {
    const messageKey = `stemsMessageShown_${audioData.id}`;
    const messageShown = localStorage.getItem(messageKey);

    if (!messageShown) {
      Dialog.info({
        closeClassName: 'hidden',
        overlayClassName: 'bg-jen-background-primary',
        canNotCloseOnMask: true,
        dialogContentClassName: 'w-[340px]  outline-none rounded-xl bg-jen-background-primary',
        contentJSX: ({ onClose }) => {
          return (
            <div className="p-[16px] flex flex-col h-full gap-4">
              <div className=" leading-6 text-jen-content-primary text-[24px] tracking-[-0.48px]">
                Your stems are being prepared for downloading.
              </div>
              <div className=" leading-5 text-jen-content-secondary text-jen-size-heading-04">
                Your stems will download shortly, there will be a slight delay while the initial download is getting ready.
              </div>
              <div
                className="mt-4 h-12 rounded-lg text-jen-content-primary-contrast text-[16px] bg-jen-orange flex justify-center items-center cursor-pointer hover:opacity-90"
                onClick={() => onClose()}
              >
                OK
              </div>
            </div>
          );
        },
      });

      localStorage.setItem(messageKey, 'true');
    }
    const { id, name, duration } = audioData;
    const maxRetries = 60;
    const retryDelay = (duration * 1000) / maxRetries;

    const pollDownloadUrl = async (retryCount = 0): Promise<string[] | undefined> => {
      if (retryCount >= maxRetries) {
        throw new Error('download status not completed, please try again');
      }

      // eslint-disable-next-line no-useless-catch
      try {
        const response = await getDownloadStemsUrl({ id });
        const { status, files } = response?.data || {};

        if (status === 'completed') {
          return files;
        } else {
          // Status is not completed, wait for retryDelay and try again
          await new Promise((resolve) => setTimeout(resolve, retryDelay));
          return pollDownloadUrl(retryCount + 1); // Retry recursively
        }
      } catch (error) {
        throw error; // Propagate error for outer catch to handle
      }
    };

    try {
      const files = await pollDownloadUrl();
      if (files?.length) {
        const downloadPromises =
          files.map((file) => downloadByUrl({ downloadUrl: transformUrlToCurrentDomain(file), audioName: name })) || [];

        await Promise.allSettled(downloadPromises);
      }
    } catch (error: any) {
      toast(<JenErrorToast error={error} />, {
        className: '!border-0 !shadow-none',
        unstyled: true,
      });
    }
  };

  return { downloadStems };
};

export const useDownloadAudio = () => {
  const download = async (audioData: AudioDto | AudioDetailDto) => {
    const { id, name } = audioData;
    try {
      const response = await getDownloadUrl({ id });
      const { source_file } = response?.data || {};
      if (source_file) {
        await downloadByUrl({ downloadUrl: transformUrlToCurrentDomain(source_file), audioName: name });
      } else {
        toast(<JenErrorToast error={new Error('download status not completed')} />, {
          className: '!border-0 !shadow-none',
          unstyled: true,
        });
      }
    } catch (error: any) {
      toast(<JenErrorToast error={error} />, {
        className: '!border-0 !shadow-none',
        unstyled: true,
      });
    }
  };

  return { download };
};

const downloadByUrl = async ({ downloadUrl, audioName }: { downloadUrl: string; audioName: string }) => {
  const response = await fetch(downloadUrl);
  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  const { name, format } = getFileInfo(downloadUrl);
  a.href = url;
  a.download = `${audioName}_${name}.${format}`;
  a.click();
  window.URL.revokeObjectURL(url);
  toast(<JenErrorToast error={new Error('Download success')} />, {
    className: '!border-0 !shadow-none',
    unstyled: true,
  });
};

function getFileInfo(url: string): { name: string; format: string } {
  const cleanUrl = url.split('?')[0];

  const lastSlashIndex = cleanUrl.lastIndexOf('/');

  const fileNameWithFormat = cleanUrl.substring(lastSlashIndex + 1);

  const lastDotIndex = fileNameWithFormat.lastIndexOf('.');

  if (lastDotIndex === -1 || lastDotIndex === fileNameWithFormat.length - 1) {
    return { name: fileNameWithFormat, format: 'mp3' };
  }

  const fileName = fileNameWithFormat.substring(0, lastDotIndex);
  const format = fileNameWithFormat.substring(lastDotIndex + 1);

  return { name: fileName, format: format };
}
